// @ts-nocheck
import _ from 'lodash';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, Box } from '@material-ui/core';
import { useAlert, useHistoryState, useIfMounted, useOnMount } from '../../../common/hooks';
import { AlertSeverity } from '../../../common/providers';
import { AsyncUtils } from '../../../common/utils';
import { EmptyState, LoadingWrapper, Panel } from '../../../common/components';
import { selectPendingActions } from '../../../common/state/pending';
import { selectServiceMessages, selectPlatforms } from '../state/messageSelectors';
import {
  fetchServiceMessages,
  fetchPlatforms,
  createServiceMessage,
  updateServiceMessage,
} from '../state/messageActions';
import ServiceMessageList from './partials/ServiceMessageList';

const useStyles = makeStyles(() => ({
  root: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  panel: {
    flex: '1',
    height: '100%',
    paddingTop: '68px',
    paddingLeft: '0',
    paddingRight: '0',
    paddingBottom: '0',
  },
}));

function MessagesPage() {
  const [loading, setLoading] = useHistoryState('loading', true);
  const [filterParams, setFilterParams] = useHistoryState('filterParams', {
    platform: 'all',
    state: 'open',
  });
  const [t] = useTranslation();
  const { showAlert } = useAlert();
  const serviceMessages = useSelector(selectServiceMessages);
  const platforms = useSelector(selectPlatforms);
  const pendingActions = useSelector(selectPendingActions);
  const classes = useStyles();
  const ifMounted = useIfMounted();
  const dispatch = useDispatch();

  useOnMount(() => {
    async function handleFetchData() {
      try {
        if (!serviceMessages) {
          setLoading(true);
        }
        await AsyncUtils.executeAll([dispatch(fetchServiceMessages()), dispatch(fetchPlatforms())]);
      } catch (e) {
        showAlert({
          title: t('serviceMessage.serviceMessagesFetchError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      } finally {
        ifMounted(() => setLoading(false));
      }
    }
    handleFetchData();
  });

  const handleCreateServiceMessage = useCallback(
    async messageData => {
      try {
        await dispatch(createServiceMessage(messageData));
        await dispatch(fetchServiceMessages());
        showAlert({
          title: t('common.success'),
          message: t('serviceMessage.serviceMessageCreateSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('serviceMessage.serviceMessageCreateError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      }
    },
    [dispatch, showAlert, t]
  );

  const handleUpdateServiceMessage = useCallback(
    async messageData => {
      try {
        await dispatch(updateServiceMessage(messageData));
        await dispatch(fetchServiceMessages());
        showAlert({
          title: t('common.success'),
          message: t('serviceMessage.serviceMessageUpdateSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('serviceMessage.serviceMessageUpdateError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      }
    },
    [dispatch, showAlert, t]
  );

  const handleFilterChange = useCallback(
    params => {
      setFilterParams({
        ...filterParams,
        ...params,
      });
    },
    [filterParams, setFilterParams]
  );

  return (
    <Box id="ServiceMessages-container" className={classes.root}>
      <LoadingWrapper loading={loading}>
        <Panel
          id="ServiceMessages-panel"
          title={t('serviceMessage.serviceMessages')}
          className={classes.panel}
        >
          {!serviceMessages ? (
            <EmptyState
              variant="unavailable"
              message={t('serviceMessage.serviceMessagesNotAvailable')}
            />
          ) : (
            <ServiceMessageList
              data={serviceMessages}
              platforms={_.defaultTo(platforms, [])}
              filterParams={filterParams}
              onCreateServiceMessage={handleCreateServiceMessage}
              onUpdateServiceMessage={handleUpdateServiceMessage}
              onFilterChange={handleFilterChange}
              actionsDisabled={!_.isEmpty(pendingActions)}
            />
          )}
        </Panel>
      </LoadingWrapper>
    </Box>
  );
}

export default MessagesPage;
